import type { ReactNode } from 'react'

import { Text, chakra } from '@enechain/ecloud-designsystem'

type Props = {
  children: ReactNode
  /**
   * 指定時は `<label htmlFor={htmlFor}>` として render し、 対応する id を持つ
   * input と accessible name を association する (screen reader / `getByLabelText`
   * が正しく解決できるようにする)。 未指定時は従来の `<Text>` 挙動を維持
   * (input との association が不要な純表示 label の caller 向け)。
   */
  htmlFor?: string
}

/**
 * OCCTO マスタ系サブフォーム (リピーター行) のフィールドラベル。
 *
 * textStyle="label" (md/semibold) は design/admin/occto.html:702
 * `.occto-member-fields > div > label` = md/600 が正。旧実装の
 * fontSize="xs" (10px/500) からの意図的視覚変更 (10px→14px、design SoT 準拠、
 * ユーザー承認済み、2026-07-11 design-parity-verifier の実測で design と
 * exact 一致を確認済み)。値・コメントの変更は本部品 1 箇所で行う。
 *
 * a11y: `htmlFor` を渡すと `<label>` として render し、 対応 id の input と
 * label association を成立させる (PR #4772 review 指摘: `CompactCivilDateField`
 * 経由の日付入力は visible input が aria-hidden、 native date input は id
 * のみで自前 label が無かった)。 未指定 caller は現行 Text 挙動を維持する
 * (post-migration の段階的な a11y 是正を許容)。
 */
export const SubFormLabel = ({
  children,
  htmlFor,
}: Props): React.ReactElement =>
  htmlFor !== undefined ? (
    <chakra.label
      textStyle="label"
      display="block"
      marginBottom={1}
      htmlFor={htmlFor}
    >
      {children}
    </chakra.label>
  ) : (
    <Text textStyle="label" marginBottom={1}>
      {children}
    </Text>
  )
